import type { FmbWorkshopStep, FrauMeetsBoerseConfig } from './types';

/**
 * Basis-URL der Börsencampus-App je Umgebung (leer = relativer, same-origin Link).
 * Wird benötigt, weil die Landingpage in Produktion auf der eigenen Domain
 * fraumeetsboerse.de läuft, dort aber /auth/register nicht existiert — der
 * Register-Link muss also absolut auf die Börsencampus-Domain zeigen.
 */
const BOERSENCAMPUS_BASE = (import.meta.env.VITE_BOERSENCAMPUS_URL ?? '').replace(/\/$/, '');

/** Preis des kompletten Workshops — einzige Quelle für alle Kauf-Buttons und die Preis-Überschrift */
const WORKSHOP_PRICE = '497 €';

/**
 * Schritt 1 ist das Geschenk dieser Seite und wird zusätzlich als Überschrift
 * über der Videoliste verwendet — deshalb als eigene Konstante.
 */
const WORKSHOP_STEP_ONE: FmbWorkshopStep = {
  label: 'Schritt 1',
  title: 'Klarheit & Basis schaffen',
  description: 'Du weißt, wo du stehst und wo du hinwillst. Das Fundament liegt.',
  note: 'hast du geschenkt bekommen'
};

/** Die vier Schritte des Umsetzungsworkshops — identisch auf Landingpage und Videoseite */
const WORKSHOP_STEPS: FmbWorkshopStep[] = [
  WORKSHOP_STEP_ONE,
  {
    label: 'Schritt 2',
    title: 'Chartanalyse & Kapitalaufbau',
    description:
      'Du lernst, Kurse zu lesen und gute Einstiege zu erkennen — ruhig und nachvollziehbar. Danach starrst du nicht mehr ratlos auf Zahlen, sondern verstehst, was du siehst.',
    note: null
  },
  {
    label: 'Schritt 3',
    title: 'Passives vs. aktives Investieren',
    description:
      'Du findest heraus, welcher Weg zu deinem Leben passt. (ETFs sind günstige Fonds — mit einem Kauf investierst du breit gestreut, ohne dich täglich kümmern zu müssen.) Danach weißt du, wie du dein Geld für dich arbeiten lässt.',
    note: null
  },
  {
    label: 'Schritt 4',
    title: 'Aktives Investieren — Umsetzen & Routine aufbauen',
    description:
      'Aus Wissen wird Gewohnheit. Du baust deine eigene, feste Routine auf — damit du dranbleibst, auch wenn der Alltag dazwischenkommt.',
    note: null
  }
];

/**
 * Flache Kurzfassung für die Checkliste der Landingpage — abgeleitet, damit
 * die Schrittnamen auf Landingpage und Videoseite nie auseinanderlaufen.
 */
const WORKSHOP_STEP_LABELS: string[] = WORKSHOP_STEPS.map((step) => `${step.label}: ${step.title}`);

/**
 * Zentrale Konfiguration der "Frau meets Börse"-Landingpage.
 * Alle Texte, Farben und Bilder der Seite werden ausschließlich hier gepflegt —
 * die Komponenten unter src/components/frauMeetsBoerse/ enthalten keine Inhalte.
 *
 * Bilder: Dateien nach public/fmb/ legen und den Pfad als src eintragen
 * (z. B. src: '/fmb/hero.jpg'). src: null zeigt einen Platzhalter an.
 */
export const FRAU_MEETS_BOERSE_CONFIG: FrauMeetsBoerseConfig = {
  pageTitle: 'Frau meets Börse – Kostenfreies Online-Webinar für Frauen',
  routePath: '/frau-meets-boerse',
  externalHosts: ['fraumeetsboerse.de', 'www.fraumeetsboerse.de'],
  // Plan FRAU_MEETS_BOERSE entspricht dem SubscriptionType in src/models/subscription.ts.
  registerUrl: `${BOERSENCAMPUS_BASE}/auth/register?plan=FRAU_MEETS_BOERSE`,
  videosPath: '/webinar',
  heroVideoUrl:
    'https://iframe.mediadelivery.net/embed/491441/33e995d0-fc5c-4640-80a1-eebc9d8366df?autoplay=true&muted=true&captions=true',

  colors: {
    bg: '#FAF7F2',
    ink: '#211C18',
    accent: '#8C2F4B',
    accentDark: '#6F2039',
    accentLight: '#D98BA4',
    muted: '#57504A',
    faint: '#8A8074',
    label: '#A89C8C',
    badgeBorder: '#D9CDBC',
    border: '#E7DFD2',
    borderWarm: '#E2DACE',
    borderCard: '#EAE2D5',
    cardDivider: '#F0E9DD',
    sectionBg: '#F2EBE0',
    darkBorder: '#3B342C',
    darkText: '#E8E1D5',
    darkMuted: '#CFC6B9',
    cardText: '#38322D',
    stars: '#C9952F',
    ctaKicker: '#E9B7C7',
    ctaText: '#F2D7E0',
    ctaPs: '#DBA8BB',
    ctaButtonHover: '#F7E3EA',
    shadowHero: 'rgba(33, 28, 24, 0.14)',
    shadowCta: 'rgba(140, 47, 75, 0.25)',
    shadowCard: 'rgba(33, 28, 24, 0.06)',
    shadowTeam: 'rgba(33, 28, 24, 0.12)'
  },

  images: {
    logo: {
      src: '/fmb/logo-fraumeetsboerse.png',
      alt: 'frau meets börse – Sven Bolz Trading Academy',
      placeholderLabel: 'Logo: frau meets börse'
    },
    hero: {
      src: 'https://i.vimeocdn.com/video/2030610363-be2f13166f740fcdf00cf5278768e80c8670c25b0f518ba7a1c92f700329619f-d?mw=1760&mh=990',
      alt: 'Frau meets Börse Webinar – Video-Vorschau',
      placeholderLabel: 'Video-Thumbnail oder Hero-Bild (16:9)'
    },
    team: {
      src: '/fmb/team-miriam-sven.png',
      alt: 'Miriam & Sven',
      placeholderLabel: 'Foto von Miriam & Sven (Hochformat)'
    },
    press: [
      {
        src: '/fmb/presse-finanzkongress.png',
        alt: 'Finanzkongress.de',
        placeholderLabel: 'Logo: Finanzkongress'
      },
      {
        src: '/fmb/presse-wirtschaftswoche.png',
        alt: 'WirtschaftsWoche',
        placeholderLabel: 'Logo: WirtschaftsWoche'
      },
      {
        src: '/fmb/presse-focus-money.png',
        alt: 'Focus Money',
        placeholderLabel: 'Logo: Focus Money'
      }
    ]
  },

  wordmark: {
    pre: 'frau',
    bold: 'meets',
    post: 'börse'
  },
  headerCtaLabel: 'Zu den gratis Umsetzungsvideos\n',

  hero: {
    badge: 'Kostenfreies Online-Webinar für Frauen',
    titleLeading: 'Du hast alles im Griff. ',
    titleAccent: 'Außer dein Geld?',
    paragraph:
      "Jahrelang für andere gesorgt. Funktioniert. Durchgepowert. Aber wenn's ums Geld geht: Panik, Unsicherheit, Aufschieben. In knapp 20 Minuten zeigen wir dir den klaren, verständlichen Einstieg in die Börse — ohne Fachchinesisch, ohne Druck, ohne Vorwissen.",
    videoLeadIn: 'Das komplette Webinar läuft direkt hier — Ton einschalten und loslegen.',
    videoTrustLine: '100 % kostenfrei · Kein Vorwissen nötig · Jederzeit pausieren & weiterschauen',
    delayedCta: {
      label: 'Jetzt die ersten Umsetzungsvideos gratis ansehen →',
      microcopy: 'Dein nächster Schritt nach dem Webinar — die ersten Videos des 4-Schritte-Umsetzungsworkshops, geschenkt.',
      revealAfterSeconds: 950
    }
  },

  pressLabel: 'Bekannt aus',

  benefits: {
    sectionNumber: '01',
    heading: 'Was du aus dem Webinar mitnimmst',
    items: [
      {
        kicker: 'Erkennen',
        title: 'Warum Finanzen dein Thema sind',
        text: 'Du merkst, warum es sich nicht mehr lohnt, das Thema Geld wegzuschieben — und welche typischen Schwierigkeiten dir bisher im Weg standen.'
      },
      {
        kicker: 'Umdenken',
        title: 'Die Glaubenssätze, die dich ausbremsen',
        text: '„Investieren ist riskant", „das ist nichts für mich", „dafür ist es zu spät": Du erkennst, welche dieser Sätze dich zurückhalten — und warum sie so nicht stimmen.'
      },
      {
        kicker: 'Loslegen',
        title: 'Nie zu spät — der beste Moment ist jetzt',
        text: 'Egal wie alt du bist oder wo du stehst: Du gehst mit der Gewissheit raus, dass du anfangen kannst. Und dass der beste Zeitpunkt dafür heute ist.'
      }
    ]
  },

  problem: {
    kicker: '02 · Das echte Risiko',
    quoteLeading: '„Ich dachte immer: Ich bin nicht der Typ für sowas. ',
    quoteAccent: 'Jetzt investiere ich selbstbewusst.',
    attribution: '— Eine von über 500 Frauen, die sich getraut haben',
    paragraph:
      'Tausende Frauen schieben das Thema Finanzen vor sich her — bis es zu spät ist. Und das Allerschlimmste: Zeit ist beim Vermögensaufbau dein größter Hebel. Jedes Jahr Warten verschenkst du. ',
    paragraphBold: 'Inflation frisst dein Erspartes. Zinsen retten dich nicht. Sparen reicht längst nicht mehr.',
    risks: [
      {
        label: 'Altersarmut',
        text: 'Jeden Cent zweimal umdrehen — trotz 40 Jahren Arbeit.'
      },
      {
        label: 'Abhängigkeit',
        text: 'Entscheidungen über dein Geld, die du nicht selbst triffst.'
      },
      {
        label: 'Unwissen',
        text: 'Gekümmert um alle — außer um dich selbst.'
      }
    ]
  },

  solution: {
    kicker: '03 · Die Lösung',
    headingLeading: "Deshalb gibt's ",
    headingAccent: 'Frau meets Börse.',
    paragraph:
      'Ein kostenfreies Webinar für Frauen, die keine Lust mehr auf das finanzielle Labyrinth haben. Ehrlich und verständlich — geleitet von Sven & Miriam, echten Praktikern statt einfaches Buchwissen. In knapp 20 Minuten, direkt oben auf dieser Seite, machen wir dich wach fürs Thema Geld. Aber wach sein reicht uns nicht. Deshalb bekommst du direkt im Anschluss unsere ersten Umsetzungsvideos geschenkt — die dir helfen, ins Handeln zu kommen und dir wirklich klar zu werden, wo du hinwillst. Kein Zuschauen, sondern dein erster echter Schritt.',
    ctaLabel: 'Jetzt Umsetzungsvideos gratis ansehen',
    ctaSubline: 'Über 500 Frauen in der Community',
    listLabel: 'Für Frauen, die …',
    listItems: [
      'endlich verstehen wollen, wie Geldanlage funktioniert',
      'konkrete Strategien statt Ratschläge suchen',
      'selbst entscheiden wollen, wie ihre finanzielle Zukunft aussieht',
      'mit wenig Kapital, aber klarer Struktur starten möchten'
    ]
  },

  testimonials: {
    sectionNumber: '04',
    heading: 'Stimmen aus der Community',
    ratingNote: '★★★★★  „SEHR GUT" auf ProvenExpert',
    video: {
      embedUrl: 'https://iframe.mediadelivery.net/embed/491441/760eec23-4ccb-4c88-87c3-167264ec6075',
      thumbnail: {
        src: 'https://vz-48e01382-f1c.b-cdn.net/760eec23-4ccb-4c88-87c3-167264ec6075/thumbnail.jpg',
        alt: 'Dr. Yasmina über ihre Zeit bei Frau meets Börse – Video-Vorschau',
        placeholderLabel: 'Video-Testimonial: Dr. Yasmina'
      },
      initial: 'Y',
      name: 'Dr. Yasmina',
      meta: 'Ehemalige Teilnehmerin'
    },
    items: [
      {
        image: {
          src: '/fmb/testimonial-yasmina.png',
          alt: 'ProvenExpert-Bewertung von Yasmina: 5,00 von 5 – SEHR GUT',
          placeholderLabel: 'Bewertung von Yasmina'
        },
        initial: 'Y',
        name: 'Yasmina',
        meta: 'Ehemalige Teilnehmerin'
      },
      {
        image: {
          src: '/fmb/testimonial-brigitte.png',
          alt: 'ProvenExpert-Bewertung von Brigitte: 5,00 von 5 – SEHR GUT',
          placeholderLabel: 'Bewertung von Brigitte'
        },
        initial: 'B',
        name: 'Brigitte',
        meta: 'Ehemalige Teilnehmerin'
      },
      {
        image: {
          src: '/fmb/testimonial-madeleine.png',
          alt: 'Nachricht von Madeleine aus der Frau-meets-Börse-Community',
          placeholderLabel: 'Nachricht von Madeleine'
        },
        initial: 'M',
        name: 'Madeleine',
        meta: 'Ehemalige Teilnehmerin'
      },
      {
        image: {
          src: '/fmb/testimonial-claudia-d.png',
          alt: 'Nachricht von Claudia D. nach dem Kurs',
          placeholderLabel: 'Nachricht von Claudia D.'
        },
        initial: 'C',
        name: 'Claudia D.',
        meta: 'Ehemalige Teilnehmerin'
      },
      {
        image: {
          src: '/fmb/testimonial-claudia-s.png',
          alt: 'Nachricht von Claudia S. nach dem Kurs',
          placeholderLabel: 'Nachricht von Claudia S.'
        },
        initial: 'C',
        name: 'Claudia S.',
        meta: 'Ehemalige Teilnehmerin'
      }
    ]
  },

  nextStep: {
    kicker: '05 · Nach dem Webinar',
    headingLeading: 'Wissen allein verändert nichts. ',
    headingAccent: 'Umsetzung schon.',
    paragraph:
      'Im Webinar verstehst du das Warum und das Was. Aber der Schritt, der wirklich zählt, ist das Wie. Genau dafür gibt es den 4-Schritte-Umsetzungsworkshop — und die ersten Videos bekommst du geschenkt. Schau sie dir an, arbeite mit, und entscheide erst danach, ob du weitergehen willst.',
    ctaLabel: 'Jetzt Umsetzungsvideos gratis freischalten',
    ctaSubline: 'Kostenfrei · Sofortiger Zugang · Kein Abo',
    listLabel: 'Die 4 Schritte',
    listItems: WORKSHOP_STEP_LABELS
  },

  hosts: {
    kicker: '06 · Deine Hosts',
    headingLeading: 'Miriam & Sven — ',
    headingAccent: 'echte Praktiker, keine Typen im Anzug.',
    paragraph:
      'Klar, verständlich, praxisnah: Wir zeigen dir, warum gerade Frauen jetzt investieren sollten, welche Geldfehler du dir sparen kannst — und wie du den ersten Schritt zu deiner finanziellen Freiheit machst. Jederzeit online abrufbar — wann immer es in deinen Alltag passt.',
    stats: [
      { value: '500+', label: 'Frauen erfolgreich absolviert' },
      { value: '20 Min', label: 'Online-Webinar' },
      { value: '0 €', label: '100 % kostenfrei' }
    ]
  },

  ctaBanner: {
    kicker: 'Dein nächster Schritt',
    headingLeading: 'Deine finanzielle Unabhängigkeit startet ',
    headingAccent: 'jetzt.',
    paragraph:
      'Du hast das Webinar gesehen? Dann geh den nächsten Schritt: Hol dir die ersten Videos des 4-Schritte-Umsetzungsworkshops — kostenfrei und sofort verfügbar.',
    ctaLabel: 'Jetzt Umsetzungsvideos gratis ansehen',
    psLine: 'P.S.: Jede Woche, die du abwartest, kostet dich Geld. Lass es nicht noch eine weitere sein.'
  },

  videosPage: {
    badge: 'Geschenk: Deine ersten Umsetzungsvideos',
    titleLeading: 'Schritt 1 des 4-Schritte-Umsetzungsworkshops — ',
    titleAccent: 'geschenkt.',
    paragraph:
      'Hier startet die Umsetzung: In zehn kurzen Videos schaffst du Klarheit und die Basis für deinen Vermögensaufbau — und im letzten Video erfährst du, wie es mit den Schritten 2 bis 4 weitergeht. Am Ende weißt du genau, wo du finanziell hinwillst. Kostenfrei, ohne Anmeldung — schau sie dir in deinem Tempo an, so oft du möchtest.',
    microcopy: '100 % kostenfrei · Kein Abo · Sofort verfügbar',
    backLabel: '← Zurück zum Webinar',
    stepOne: {
      heading: 'Das machst du im Schritt 1:',
      items: [
        'Du definierst dein Zielbild: Wer willst du sein, was willst du tun, was willst du haben?',
        'Du ermittelst dein Vermögen sowie deine monatlichen Einnahmen und Ausgaben (inklusive Schulden, falls vorhanden).',
        'Du berechnest deinen Wohlstandsbetrag für finanzielle Sicherheit, Unabhängigkeit und Freiheit (inklusive der Formel-Ansätze).',
        'Du füllst deine Motivationstabelle aus, damit du schwarz auf weiß siehst, wofür du losgehst.'
      ],
      note: '📌 Hinweis zur Motivationstabelle: Du kannst deine Motivationstabelle direkt online ausrechnen oder dir die Datei herunterladen und sie so ausfüllen, wie wir es im Video zeigen. Die Links findest du unten. Nimm dir dafür einen Moment Zeit – sie hilft dir, deine Ziele noch klarer zu sehen und deinen eigenen Weg bewusster festzulegen.',
      workbook: {
        label: 'Workbook zu Schritt 1 herunterladen (PDF)',
        href: '/fmb/fmb-workbook-schritt-1.pdf',
        kind: 'download',
        // Ohne | und : im Namen — die sind in Windows-/macOS-Dateinamen unzulässig
        // und werden vom Browser stillschweigend durch _ ersetzt.
        downloadName: 'FmB Umsetzungsworkshop – Workbook Schritt 1.pdf'
      },
      tableLinks: [
        {
          label: 'Motivationstabelle online ausrechnen',
          href: 'https://www.svenbolz.com/motivationstabelle',
          kind: 'external'
        },
        {
          // Legt eine eigene Kopie in Google Drive an — setzt ein Google-Konto voraus
          label: 'Als Google-Tabelle kopieren',
          href: 'https://docs.google.com/spreadsheets/d/1eZMTaia15jahvS_DBuSM-OfPODS__8rC/copy',
          kind: 'external'
        },
        {
          // Direkter Export — lädt ohne Google-Konto herunter
          label: 'Als Excel herunterladen',
          href: 'https://docs.google.com/spreadsheets/d/1eZMTaia15jahvS_DBuSM-OfPODS__8rC/export?format=xlsx',
          kind: 'external'
        }
      ]
    },
    anchorLine: 'Diese 10 Videos sind Schritt 1 von 4 — plus ein Ausblick, wie es weitergeht.',
    stepLabel: `${WORKSHOP_STEP_ONE.label} · ${WORKSHOP_STEP_ONE.title}`,
    stepNote: 'Zehn Videos plus Ausblick — am besten der Reihe nach.',
    videos: [
      {
        title: 'Willkommen · So findest du dich im Kurs zurecht',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/c263bbf9-ceef-4539-8ab0-12cf28558972',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/c263bbf9-ceef-4539-8ab0-12cf28558972/thumbnail_fe9db6f2.jpg',
          alt: 'So findest du dich im Kurs zurecht – Video-Vorschau',
          placeholderLabel: 'So findest du dich im Kurs zurecht'
        },
        showStepOneResources: true
      },
      {
        title: '1 · Was willst DU wirklich?',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/051606b7-7520-419f-b10c-907b19ed2f35',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/051606b7-7520-419f-b10c-907b19ed2f35/thumbnail_8bced2d8.jpg',
          alt: 'Was willst DU wirklich? – Video-Vorschau',
          placeholderLabel: 'Was willst DU wirklich?'
        }
      },
      {
        title: '2 · Deine Vision: SEIN, TUN, HABEN',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/c22a0953-bba1-4aaf-a50d-808f4f257d4e',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/c22a0953-bba1-4aaf-a50d-808f4f257d4e/thumbnail_939d0321.jpg',
          alt: 'Deine Vision: SEIN, TUN, HABEN – Video-Vorschau',
          placeholderLabel: 'Deine Vision: SEIN, TUN, HABEN'
        }
      },
      {
        title: '3 · Was kostet dein Wunschleben?',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/56aa8a43-0b14-48d3-a577-8839a2e26ae2',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/56aa8a43-0b14-48d3-a577-8839a2e26ae2/thumbnail_9404b50c.jpg',
          alt: 'Was kostet dein Wunschleben? – Video-Vorschau',
          placeholderLabel: 'Was kostet dein Wunschleben?'
        }
      },
      {
        title: '4 · Finanzielle Sicherheit: Dein persönlicher Puffer',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/b7216ebb-0778-4290-9f48-26b8446cfd39',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/b7216ebb-0778-4290-9f48-26b8446cfd39/thumbnail_620fa995.jpg',
          alt: 'Finanzielle Sicherheit: Dein persönlicher Puffer – Video-Vorschau',
          placeholderLabel: 'Finanzielle Sicherheit: Dein persönlicher Puffer'
        }
      },
      {
        title: '5 · Finanzielle Unabhängigkeit: Wenn deine Rendite die Fixkosten deckt',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/17daf072-7a40-42a6-afaa-334ba5f7970b',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/17daf072-7a40-42a6-afaa-334ba5f7970b/thumbnail_06dd3100.jpg',
          alt: 'Finanzielle Unabhängigkeit: Wenn deine Rendite die Fixkosten deckt – Video-Vorschau',
          placeholderLabel: 'Finanzielle Unabhängigkeit: Wenn deine Rendite die Fixkosten deckt'
        }
      },
      {
        title: '6 · Finanzielle Freiheit: Was brauchst du für dein Wunschleben?',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/2950f177-8afa-4541-9825-31a462cbddce',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/2950f177-8afa-4541-9825-31a462cbddce/thumbnail_c3805194.jpg',
          alt: 'Finanzielle Freiheit: Was brauchst du für dein Wunschleben? – Video-Vorschau',
          placeholderLabel: 'Finanzielle Freiheit: Was brauchst du für dein Wunschleben?'
        }
      },
      {
        title: '7 · Dein Weg zum Vermögensaufbau',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/7b7de5af-e765-4685-8bc8-b3c0852508b0',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/7b7de5af-e765-4685-8bc8-b3c0852508b0/thumbnail_efdae545.jpg',
          alt: 'Dein Weg zum Vermögensaufbau – Video-Vorschau',
          placeholderLabel: 'Dein Weg zum Vermögensaufbau'
        }
      },
      {
        title: '8 · So arbeitet der Zinseszins für dich',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/32f0b530-cbd0-4cb4-afc3-5ccb93fe6888',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/32f0b530-cbd0-4cb4-afc3-5ccb93fe6888/thumbnail_bc3409c6.jpg',
          alt: 'So arbeitet der Zinseszins für dich – Video-Vorschau',
          placeholderLabel: 'So arbeitet der Zinseszins für dich'
        }
      },
      {
        title: '9 · Von der Vision zum konkreten Plan',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/4410b396-3eba-4705-9325-59d706f95c86',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/4410b396-3eba-4705-9325-59d706f95c86/thumbnail_57945566.jpg',
          alt: 'Von der Vision zum konkreten Plan – Video-Vorschau',
          placeholderLabel: 'Von der Vision zum konkreten Plan'
        }
      },
      {
        title: '10 · Dein Umsetzungsplan für Schritt 1',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/e78d6d72-dede-4f37-8562-606c4beead7c',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/e78d6d72-dede-4f37-8562-606c4beead7c/thumbnail_8a9498f0.jpg',
          alt: 'Dein Umsetzungsplan für Schritt 1 – Video-Vorschau',
          placeholderLabel: 'Dein Umsetzungsplan für Schritt 1'
        }
      },
      {
        title: '11 · Wie es weitergeht: Schritt 2 bis 4',
        embedUrl: 'https://iframe.mediadelivery.net/embed/491441/56808083-2dea-4336-924f-d4b50bf98541',
        thumbnail: {
          src: 'https://vz-48e01382-f1c.b-cdn.net/56808083-2dea-4336-924f-d4b50bf98541/thumbnail_47bd7bd3.jpg',
          alt: 'Wie es weitergeht: Schritt 2 bis 4 – Video-Vorschau',
          placeholderLabel: 'Wie es weitergeht: Schritt 2 bis 4'
        }
      }
    ],
    nextButtonLabel: 'Weiter zum nächsten Video',
    lastButtonLabel: 'Weiter: Dein nächster Schritt',
    bridge: {
      kicker: 'Wie es weitergeht',
      paragraph:
        'Jetzt weißt du, WO du hinwillst. Aber Klarheit allein baut noch kein Vermögen auf. Schritt 1 hast du geschafft — die Schritte 2 bis 4 zeigen dir das WIE. Von der ersten konkreten Umsetzung bis zu dem Punkt, an dem Investieren für dich ganz normaler Alltag ist.',
      ctaLabel: `Ja, ich gehe alle 4 Schritte (${WORKSHOP_PRICE})`,
      microcopy: 'Sofortiger Zugang · Zugang für immer · kein Abo'
    },
    offer: {
      workshop: {
        kicker: 'Der Workshop',
        headingLeading: 'In 4 Schritten von Klarheit zu ',
        headingAccent: 'deinem eigenen Vermögen.',
        subline: 'Der 4-Schritte-Umsetzungsworkshop — begleitet von Miriam, in deinem Tempo.',
        steps: WORKSHOP_STEPS
      },
      miriam: {
        kicker: 'Deine Begleitung',
        headingLeading: 'Miriam war da, ',
        headingAccent: 'wo du gerade bist.',
        paragraph:
          'Kein Finanzstudium, kein Vermögen im Rücken — Miriam hat selbst bei null angefangen, mit denselben Zweifeln. Heute ist sie Traderin und begleitet dich durch jeden der vier Schritte. Sie erklärt dir nichts, was sie nicht selbst durchgemacht hat.',
        image: {
          src: '/fmb/miriam.png',
          alt: 'Miriam',
          placeholderLabel: 'Foto von Miriam (Quadratisch)'
        }
      },
      deliverables: {
        kicker: 'Was du bekommst',
        heading: 'Das ist alles dabei:',
        items: [
          'Alle 4 Schritte des Workshops — klar aufgebaut, verständlich, ohne Fachchinesisch',
          'Zugang für immer — einmal deins, immer deins. In deinem Tempo, so oft du willst.',
          'Dein persönliches 30-Minuten-Gespräch mit Miriam — von Frau zu Frau, konkret zu deiner Situation'
        ]
      },
      price: {
        kicker: 'Der Preis',
        headingLeading: 'Einmalig ',
        headingAccent: WORKSHOP_PRICE,
        paragraph:
          'Kein Abo, keine Folgekosten. Schritt 1 hast du kostenlos bekommen und selbst gesehen, wie hier gearbeitet wird. Die Schritte 2 bis 4 sind der eigentliche Weg — einmal investiert, für immer deins.',
        contextLabel: 'Zur Einordnung',
        contextParagraph:
          'Frauen bekommen in Deutschland im Schnitt rund ein Drittel weniger eigene Rente als Männer. Über ein ganzes Rentnerleben summiert sich das auf zehntausende Euro. Gemessen daran ist nicht der Workshop teuer — teuer ist, weiter nichts zu tun.',
        ctaLabel: `Ja, ich sichere mir den Workshop (${WORKSHOP_PRICE})`,
        microcopy: 'Sofortiger Zugang zu allen Modulen · Zugang für immer · kein Abo'
      },
      proof: {
        kicker: 'Aus der Community',
        headingLeading: 'Über 500 Frauen ',
        headingAccent: 'sind schon dabei.'
      },
      faq: {
        heading: 'Häufige Fragen',
        items: [
          {
            question: 'Brauche ich Vorwissen, um mitzukommen?',
            answer:
              'Nein. Der Workshop ist für absolute Einsteigerinnen gemacht — du fängst wirklich von vorne an. Miriam erklärt alles in normaler Sprache, Schritt für Schritt. Wenn du einen Kontoauszug lesen kannst, kommst du auch hier mit.'
          },
          {
            question: 'Bin ich zu spät dran?',
            answer:
              'Nein. Der beste Zeitpunkt war vielleicht vor zehn Jahren — der zweitbeste ist heute. Es geht nicht darum, verlorene Zeit aufzuholen, sondern ab jetzt das Richtige zu tun.'
          },
          {
            question: 'Ich habe nur wenig übrig — lohnt sich das?',
            answer:
              'Gerade dann. Du brauchst kein großes Startkapital, um anzufangen — du lernst, mit dem loszulegen, was für dich machbar ist. Es geht ums kluge Anfangen, nicht um große Summen.'
          },
          {
            question: 'Was, wenn ich Angst habe, etwas falsch zu machen?',
            answer:
              'Diese Angst ist normal — und der Grund, warum du es nicht allein machst. Du hast einen klaren Weg vor dir und Miriam an deiner Seite. Der größte Fehler wäre, aus Angst gar nicht erst anzufangen.'
          },
          {
            question: 'Wie läuft der Workshop ab, und wie viel Zeit brauche ich?',
            answer:
              'Du bekommst alle vier Schritte als Videos, die du dir ansehen kannst, wann und so oft du willst. Angelegt sind sie auf vier Wochen — aber du bestimmst das Tempo, und dein Zugang läuft nie ab. Kommt das Leben dazwischen, machst du einfach später weiter.'
          },
          {
            question: 'Bekomme ich konkrete Kauf-Tipps?',
            answer:
              'Der Workshop zeigt dir, wie Investieren funktioniert und wie du selbst gute Entscheidungen triffst — ehrlich und verständlich. Das ist Finanzbildung, keine persönliche Anlageberatung: Du lernst, es selbst zu können, statt dich auf Tipps zu verlassen. (Mehr dazu in unserem ',
            answerLink: {
              label: 'Risikohinweis',
              href: '/risikodisclaimer',
              textAfter: '.)'
            }
          },
          {
            question: 'Wie funktioniert das 30-Minuten-Gespräch mit Miriam?',
            answer:
              'Nach dem Kauf kannst du dich einmalig für ein persönliches 30-Minuten-Gespräch mit Miriam eintragen — von Frau zu Frau, konkret zu deiner Situation und deinen Fragen.'
          },
          {
            question: 'Kommen noch weitere Kosten auf mich zu?',
            answer: `Nein. Der Workshop kostet einmalig ${WORKSHOP_PRICE} — kein Abo, keine versteckten Folgekosten. Einmal investiert, und der Zugang bleibt für immer deiner.`
          }
        ]
      },
      cta: {
        kicker: 'Dein nächster Schritt',
        headingLeading: 'Du hast heute schon angefangen. ',
        headingAccent: 'Mach den nächsten Schritt.',
        paragraph:
          'Schritt 1 liegt hinter dir. Die Schritte 2 bis 4 zeigen dir das WIE — von der ersten konkreten Umsetzung bis zur Routine, die bleibt.',
        ctaLabel: `Jetzt den 4-Schritte-Umsetzungsworkshop sichern (${WORKSHOP_PRICE})`,
        microcopy: 'Sofortiger Zugang · Zugang für immer · kein Abo'
      }
    }
  },

  faq: {
    sectionNumber: '07',
    heading: 'Häufig gestellte Fragen',
    items: [
      {
        question: 'Für wen ist dieses Webinar geeignet?',
        answer:
          'Für Frauen, die endlich verstehen wollen, wie Geldanlage funktioniert — egal ob kompletter Neustart oder erste Erfahrungen. Wenn du das Gefühl kennst, alles im Griff zu haben außer dein Geld, bist du hier richtig.'
      },
      {
        question: 'Brauche ich Vorkenntnisse oder Startkapital?',
        answer:
          'Nein. Das Webinar ist bewusst für den Einstieg gemacht — jeder Begriff wird erklärt. Und wichtiger als die Summe ist die Struktur: Du kannst auch mit kleinem Kapital sinnvoll starten.'
      },
      {
        question: 'Was kostet die Teilnahme am Webinar?',
        answer:
          'Nichts. Das Webinar ist zu 100 % kostenfrei — keine Anmeldung, kein Abo, kein Haken. Du schaust es direkt hier auf dieser Seite.'
      },
      {
        question: 'Was passiert nach dem Webinar?',
        answer:
          'Du entscheidest. Wenn du tiefer einsteigen willst, schenken wir dir die ersten Videos unseres 4-Schritte-Umsetzungsworkshops. Dort setzt du das Gelernte konkret um — Schritt für Schritt. Erst wenn dich das überzeugt, gibt es die Möglichkeit, den kompletten Workshop zu buchen.'
      },
      {
        question: 'Wie komme ich zum Webinar?',
        answer:
          'Ganz einfach: nach oben scrollen und auf Play drücken. Keine Registrierung, kein Termin — du schaust, wann und wo es dir passt.'
      }
    ]
  },

  footer: {
    links: [
      { label: 'Impressum', href: '/impressum' },
      { label: 'Datenschutz', href: '/datenschutz' },
      { label: 'Disclaimer', href: '/risikodisclaimer' }
    ],
    copyright: '© 2026 Frau meets Börse'
  }
};

/**
 * true, wenn die App über eine der externen Frau-meets-Börse-Domains
 * (Reverse Proxy) aufgerufen wird — dort rendert jeder Pfad die Landingpage.
 */
export function isFrauMeetsBoerseHost(): boolean {
  return FRAU_MEETS_BOERSE_CONFIG.externalHosts.includes(window.location.hostname.toLowerCase());
}
